import { useEffect, useRef, useState } from "react";
import { normalize } from "../lib/voiceParsers";

// Multi-language start/stop keywords. We match prefixes after normalization.
const START_WORDS = [
  "start",
  "startuj",
  "go",
  "play",
  "begin",
  "graj",
  "los",
  "commence",
  "demarre",
  "inicio",
  "comenzar",
  "empezar",
  "старт",
  "почати",
];

const STOP_WORDS = [
  "stop",
  "stopp",
  "stoppt",
  "halt",
  "pause",
  "pauza",
  "arret",
  "arrete",
  "parar",
  "pare",
  "detener",
  "стоп",
  "стій",
  "стой",
];

function matchKeyword(words, list) {
  for (const w of words) {
    for (const k of list) {
      if (w === k || w.startsWith(k)) return true;
    }
  }
  return false;
}

// Map app language -> SpeechRecognition locale
const LANG_MAP = {
  pl: "pl-PL",
  en: "en-US",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  uk: "uk-UA",
};

export function getSRLang(appLang) {
  return LANG_MAP[appLang] || "en-US";
}

/**
 * Continuously listens to the microphone and triggers onStart/onStop
 * when the matching keyword is heard. Auto-restarts the recognition
 * session because most browsers cap each session.
 */
export function useSpeechRecognition({
  enabled,
  lang,
  onStart,
  onStop,
  onResult,
}) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const aliveRef = useRef(false);
  const onStartRef = useRef(onStart);
  const onStopRef = useRef(onStop);
  const onResultRef = useRef(onResult);

  useEffect(() => {
    onStartRef.current = onStart;
  }, [onStart]);
  useEffect(() => {
    onStopRef.current = onStop;
  }, [onStop]);
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    const SR =
      typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    setSupported(Boolean(SR));
  }, []);

  useEffect(() => {
    const SR =
      typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return undefined;

    if (!enabled) {
      aliveRef.current = false;
      if (recRef.current) {
        try {
          recRef.current.stop();
        } catch {
          /* ignore */
        }
        recRef.current = null;
      }
      setListening(false);
      setError(null);
      return undefined;
    }

    const rec = new SR();
    rec.lang = lang || "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      setListening(true);
      setError(null);
    };

    rec.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript || "";
        const normalized = normalize(transcript);
        const words = normalized.split(/\s+/).filter(Boolean);
        const isFinal = result.isFinal;
        // Only inspect the last couple of words for snappier response
        const lastWords = words.slice(-4);
        // Built-in start/stop commands
        if (matchKeyword(lastWords, START_WORDS)) {
          onStartRef.current && onStartRef.current();
          continue;
        }
        if (matchKeyword(lastWords, STOP_WORDS)) {
          onStopRef.current && onStopRef.current();
          continue;
        }
        // Pass everything else to the consumer for answer recognition
        if (onResultRef.current) {
          onResultRef.current({
            transcript: normalized,
            words,
            isFinal,
          });
        }
      }
    };

    rec.onerror = (e) => {
      if (e && e.error && e.error !== "no-speech" && e.error !== "aborted") {
        setError(e.error);
      }
    };

    rec.onend = () => {
      // Auto-restart so the listener keeps running on browsers that
      // automatically end sessions after silence.
      if (aliveRef.current && recRef.current === rec) {
        try {
          rec.start();
        } catch {
          // Swallow "already started" errors
        }
      } else {
        setListening(false);
      }
    };

    recRef.current = rec;
    aliveRef.current = true;
    try {
      rec.start();
    } catch (e) {
      setError((e && e.message) || "start_failed");
    }

    return () => {
      aliveRef.current = false;
      recRef.current = null;
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
      setListening(false);
    };
  }, [enabled, lang]);

  return { supported, listening, error };
}
